import { captureException } from '@sentry/react-router'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigation } from 'react-router'

export const useViewTransition = () => {
  const navigation = useNavigation()
  const location = useLocation()

  const isTransitioning = useRef(false)
  const previousPathname = useRef(location.pathname)
  const transitionResolve = useRef<(() => void) | null>(null)
  const navigationState = useRef(navigation.state)
  navigationState.current = navigation.state

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!('startViewTransition' in document)) return

    if (navigation.state === 'idle') {
      previousPathname.current = location.pathname
      transitionResolve.current?.()
      transitionResolve.current = null
    }

    if (navigation.state === 'loading' && !isTransitioning.current && document.visibilityState !== 'hidden') {
      const targetPath = navigation.location?.pathname
      if (targetPath && targetPath !== previousPathname.current) {
        isTransitioning.current = true

        const transition = document.startViewTransition(() => {
          return new Promise<void>((resolve) => {
            // The browser may invoke the update callback after routing has already finished.
            if (navigationState.current === 'idle') resolve()
            else transitionResolve.current = resolve
          })
        })

        void transition.ready.catch((error: unknown) => {
          if (
            error instanceof DOMException &&
            error.name === 'InvalidStateError' &&
            // Firefox: ready rejects when a hidden document skips the animation.
            (error.message === 'Skipped ViewTransition due to document being hidden' ||
              // Safari: the same visibility skip uses different wording.
              error.message === 'View transition was skipped because document visibility state is hidden.')
          ) {
            return
          }
          captureException(error)
        })

        const finish = () => {
          transitionResolve.current?.()
          transitionResolve.current = null
          isTransitioning.current = false
        }
        void transition.finished.then(finish, (error: unknown) => {
          finish()
          captureException(error)
        })
      }
    }
  }, [navigation.state, navigation.location, location.pathname])
}
